// Уведомления владельцу: вопросы агентов и готовые задачи. Вкладка OMA в фоне — уведомление браузера; открыта — всплывающее в OMA.
import { useSyncExternalStore } from 'react'
import { outcomeText } from './lib/outcome.ts'
import { getLocation } from './router.tsx'
import { toast } from './toast.ts'
import type { Escalation, Message, Task } from './types.ts'
import { ESCALATION_TYPE_LABELS } from './types.ts'

const seen = new Set<number>()
const listeners = new Set<() => void>()
const PREF_KEY = 'oma:notify'

/** Пользователь может выключить уведомления в OMA, не трогая разрешение браузера. */
function readEnabled(): boolean {
  try {
    return window.localStorage.getItem(PREF_KEY) !== 'off'
  } catch {
    return true
  }
}
let enabled = readEnabled()

export function notificationsOn(): boolean {
  return enabled && permission() === 'granted'
}

export function setNotificationsEnabled(on: boolean): void {
  enabled = on
  try {
    window.localStorage.setItem(PREF_KEY, on ? 'on' : 'off')
  } catch {
    /* noop */
  }
  for (const l of listeners) l()
}

/** Состояние кнопки-колокольчика: off — выключено в OMA, default — ещё не спрашивали браузер. */
export type NotifyState = 'on' | 'off' | 'default' | 'denied' | 'unsupported'

function notifyState(): NotifyState {
  const p = permission()
  if (p === 'unsupported') return 'unsupported'
  if (p === 'denied') return 'denied'
  if (p === 'default') return 'default'
  return enabled ? 'on' : 'off'
}

export function useNotifyState(): NotifyState {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    notifyState,
    () => 'unsupported' as const,
  )
}

export const notifySupported = (): boolean => typeof window !== 'undefined' && 'Notification' in window

function permission(): NotificationPermission | 'unsupported' {
  return notifySupported() ? Notification.permission : 'unsupported'
}

export function useNotifyPermission(): NotificationPermission | 'unsupported' {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    permission,
    () => 'unsupported' as const,
  )
}

export async function enableNotifications(): Promise<NotificationPermission | 'unsupported'> {
  if (!notifySupported()) return 'unsupported'
  const p = await Notification.requestPermission()
  if (p === 'granted') enabled = true
  try {
    if (p === 'granted') window.localStorage.setItem(PREF_KEY, 'on')
  } catch {
    /* noop */
  }
  for (const l of listeners) l()
  return p
}

const doneSeen = new Set<string>()

/**
 * Агенты закончили задачу владельца. Всплывашка в OMA — всегда: открытая задача молча переезжает в «Готово»,
 * и владелец не понимает, что произошло (28.10, OMA-1013). Системное уведомление — только если он не в этой задаче.
 */
export function notifyTaskDone(t: Task): void {
  const key = `${t.id}:${t.closed_at ?? ''}`
  if (doneSeen.has(key)) return
  doneSeen.add(key)
  const { text } = outcomeText(t)
  const body = (text || t.title).replace(/\s+/g, ' ')
  const title = `${t.key} готова`
  toast(body.length > 240 ? `${body.slice(0, 239)}…` : body, 'success', { title: `${title}: ${t.title}`, taskId: t.id, ms: 15000 })
  if (getLocation().pathname === `/tasks/${t.id}`) return
  if (!notifySupported() || Notification.permission !== 'granted' || !enabled) return
  if (!document.hidden && document.hasFocus()) return
  try {
    const n = new Notification(`OMA · ${title}`, { body: `${t.title}\n${body.slice(0, 220)}`, tag: `oma-done-${t.id}` })
    n.onclick = () => {
      window.focus()
      window.location.assign(`/tasks/${t.id}`)
      n.close()
    }
  } catch {
    // уведомления недоступны — остаётся всплывающее в OMA и счётчик в меню
  }
}

const repliesSeen = new Set<number>()

/** Агент ответил владельцу в переписке задачи. Всплывашка — и в открытой задаче: ответ лежит во вкладке «Переписка», с «Обзора» его не видно. */
export function notifyOwnerReply(m: Message, agentName: string): void {
  if (m.from_type !== 'agent' || m.to_type !== 'human' || m.kind !== 'answer' || !m.task_id || repliesSeen.has(m.id)) return
  repliesSeen.add(m.id)
  const body = m.body.replace(/[*_`#>]/g, '').replace(/\s+/g, ' ').trim()
  const title = `Ответ по OMA-${m.task_id} от «${agentName}»`
  toast(body.length > 240 ? `${body.slice(0, 239)}…` : body, 'info', { title, taskId: m.task_id, ms: 15000 })
  if (getLocation().pathname === `/tasks/${m.task_id}`) return
  if (!notifySupported() || Notification.permission !== 'granted' || !enabled) return
  if (!document.hidden && document.hasFocus()) return
  try {
    const n = new Notification(`OMA · ${title}`, { body: body.slice(0, 220), tag: `oma-reply-${m.id}` })
    n.onclick = () => {
      window.focus()
      window.location.assign(`/tasks/${m.task_id}`)
      n.close()
    }
  } catch {
    // уведомления недоступны — остаётся всплывающее в OMA
  }
}

export function notifyEscalation(e: Escalation): void {
  if (e.status !== 'open' || seen.has(e.id)) return
  seen.add(e.id)
  if (!notifySupported() || Notification.permission !== 'granted' || !enabled) return
  if (!document.hidden && document.hasFocus()) return
  try {
    const n = new Notification(`OMA · ${ESCALATION_TYPE_LABELS[e.type] ?? 'вопрос'}${e.task_key ? ` · ${e.task_key}` : ''}`, {
      body: e.question.replace(/\s+/g, ' ').slice(0, 220),
      tag: `oma-escalation-${e.id}`,
    })
    n.onclick = () => {
      window.focus()
      window.location.assign(e.task_id ? `/tasks/${e.task_id}` : '/inbox')
      n.close()
    }
  } catch {
    // уведомления недоступны (например, в iOS без установки на экран) — молча пропускаем
  }
}
